import { useState, useEffect } from 'react';
import {
  Plus,
  RefreshCw,
  Eye,
  Edit2,
  Trash2,
  MoreVertical,
  PlayCircle,
  Clock,
  CalendarClock,
  AlertCircle,
  ToggleLeft,
  ToggleRight,
  Search,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { StatCard } from '../../components/ui/StatCard';
import { Table } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { DropdownMenu } from '../../components/ui/DropdownMenu';
import { RecurringEntryFormModal } from '../../components/recurring-entries/RecurringEntryFormModal';
import { RecurringEntryDetailModal } from '../../components/recurring-entries/RecurringEntryDetailModal';
import { useRecurringEntries } from '../../hooks/useRecurringEntries';
import { usePermissions } from '../../hooks/usePermissions';
import { useCurrency } from '../../hooks/useCurrency';
import toast from 'react-hot-toast';
import type { RecurringEntry, CreateRecurringEntryPayload, UpdateRecurringEntryPayload } from '../../types/recurring-entries';
import {
  RECURRENCE_FREQUENCY_LABELS,
  RECURRENCE_FREQUENCY_COLORS,
} from '../../types/recurring-entries';
import { JOURNAL_TYPES } from '../../config/accounting';

export function RecurringEntriesPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<RecurringEntry | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<string>('');

  const { can } = usePermissions();
  const canManage = can('manage_recurring_entries');
  const { formatConverted } = useCurrency();

  const {
    entries,
    loading,
    error,
    loadEntries,
    createEntry,
    updateEntry,
    deleteEntry,
    generateEntries,
    activeEntries,
    dueEntries,
  } = useRecurringEntries();

  useEffect(() => {
    const params: any = {};
    if (filterActive === 'active') params.isActive = true;
    if (filterActive === 'inactive') params.isActive = false;
    loadEntries(params);
  }, [loadEntries, filterActive]);

  // Filter locally by search term
  const filteredEntries = entries.filter(e => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return e.name.toLowerCase().includes(term)
      || (e.description || '').toLowerCase().includes(term);
  });

  const handleCreate = async (data: CreateRecurringEntryPayload) => {
    const result = await createEntry(data);
    if (result) {
      toast.success('Écriture récurrente créée');
      setShowCreateModal(false);
    }
  };

  const handleEdit = async (data: CreateRecurringEntryPayload) => {
    if (!selectedEntry) return;
    const result = await updateEntry(selectedEntry.id, data as UpdateRecurringEntryPayload);
    if (result) {
      toast.success('Écriture récurrente mise à jour');
      setShowEditModal(false);
      setSelectedEntry(null);
    }
  };

  const handleDelete = async (entry: RecurringEntry) => {
    if (!confirm(`Supprimer « ${entry.name} » ?`)) return;
    const success = await deleteEntry(entry.id);
    if (success) toast.success('Écriture récurrente supprimée');
  };

  const handleToggleActive = async (entry: RecurringEntry) => {
    const result = await updateEntry(entry.id, { isActive: !entry.isActive });
    if (result) {
      toast.success(result.isActive ? 'Écriture activée' : 'Écriture désactivée');
    }
  };

  const handleGenerate = async () => {
    if (!confirm('Générer toutes les écritures récurrentes dues ?')) return;
    const result = await generateEntries();
    if (result) {
      if (result.generated > 0) {
        toast.success(`${result.generated} écriture(s) générée(s)`);
      } else {
        toast.success('Aucune écriture à générer pour le moment');
      }
    }
  };

  const totalMonthlyAmount = activeEntries
    .filter(e => e.frequency === 'monthly')
    .reduce((sum, e) => sum + e.templateLines.reduce((s, l) => s + l.debit, 0), 0);

  const columns = [
    {
      header: 'Nom',
      accessor: (entry: RecurringEntry) => (
        <div>
          <p className="font-medium text-foreground">{entry.name}</p>
          {entry.description && (
            <p className="text-xs text-muted-foreground truncate max-w-48">{entry.description}</p>
          )}
        </div>
      ),
    },
    {
      header: 'Fréquence',
      accessor: (entry: RecurringEntry) => {
        const color = RECURRENCE_FREQUENCY_COLORS[entry.frequency];
        return (
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${color.bg} ${color.text}`}>
            {RECURRENCE_FREQUENCY_LABELS[entry.frequency]}
          </span>
        );
      },
    },
    {
      header: 'Journal',
      accessor: (entry: RecurringEntry) => {
        if (!entry.journalType) return <span className="text-muted-foreground text-xs">—</span>;
        const mapping: Record<string, keyof typeof JOURNAL_TYPES> = {
          sales: 'SALES', purchases: 'PURCHASE', bank: 'BANK', cash: 'CASH', general: 'GENERAL',
        };
        const key = mapping[entry.journalType];
        return (
          <span className="text-xs font-medium text-foreground">
            {key ? JOURNAL_TYPES[key].label : entry.journalType}
          </span>
        );
      },
    },
    {
      header: 'Prochaine exéc.',
      accessor: (entry: RecurringEntry) => {
        const next = new Date(entry.nextRunDate);
        const isDue = next <= new Date();
        return (
          <span className={`text-sm ${isDue ? 'text-warning font-semibold' : 'text-foreground'}`}>
            {next.toLocaleDateString('fr-FR')}
            {isDue && <AlertCircle className="inline h-3.5 w-3.5 ml-1 text-warning" />}
          </span>
        );
      },
    },
    {
      header: 'Montant',
      accessor: (entry: RecurringEntry) => {
        const total = entry.templateLines.reduce((s, l) => s + l.debit, 0);
        return <span className="font-mono text-sm">{formatConverted(total)}</span>;
      },
      className: 'text-right',
    },
    {
      header: 'Lignes',
      accessor: (entry: RecurringEntry) => (
        <Badge variant="default">{entry.templateLines.length}</Badge>
      ),
    },
    {
      header: 'Statut',
      accessor: (entry: RecurringEntry) => (
        <Badge variant={entry.isActive ? 'success' : 'default'}>
          {entry.isActive ? 'Actif' : 'Inactif'}
        </Badge>
      ),
    },
    {
      header: 'Actions',
      accessor: (entry: RecurringEntry) => {
        const menuItems = [
          {
            label: 'Voir les détails',
            icon: Eye,
            onClick: () => {
              setSelectedEntry(entry);
              setShowDetailModal(true);
            },
          },
          ...(canManage ? [
            {
              label: 'Modifier',
              icon: Edit2,
              onClick: () => {
                setSelectedEntry(entry);
                setShowEditModal(true);
              },
            },
            {
              label: entry.isActive ? 'Désactiver' : 'Activer',
              icon: entry.isActive ? ToggleLeft : ToggleRight,
              onClick: () => handleToggleActive(entry),
            },
            {
              label: 'Supprimer',
              icon: Trash2,
              onClick: () => handleDelete(entry),
              variant: 'danger' as const,
            },
          ] : []),
        ];

        return (
          <DropdownMenu
            trigger={
              <Button variant="ghost" size="sm" icon={MoreVertical} className="h-8 w-8 p-0" />
            }
            items={menuItems}
          />
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Écritures récurrentes</h1>
          <p className="text-sm text-muted-foreground">
            Automatisez vos écritures comptables répétitives
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && dueEntries.length > 0 && (
            <Button variant="warning" icon={PlayCircle} onClick={handleGenerate}>
              Générer ({dueEntries.length} dues)
            </Button>
          )}
          {canManage && (
            <Button variant="primary" icon={Plus} onClick={() => setShowCreateModal(true)}>
              Nouvelle récurrence
            </Button>
          )}
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total récurrences"
          value={entries.length}
          icon={CalendarClock}
        />
        <StatCard
          title="Actives"
          value={activeEntries.length}
          icon={Clock}
        />
        <StatCard
          title="Dues aujourd'hui"
          value={dueEntries.length}
          icon={AlertCircle}
        />
        <StatCard
          title="Montant mensuel"
          value={formatConverted(totalMonthlyAmount)}
          icon={RefreshCw}
        />
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm">
          {error}
        </div>
      )}

      {/* Filtres */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-3 p-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher par nom..."
              className="form-input w-full pl-9 rounded-lg border-border bg-background text-foreground text-sm"
            />
          </div>
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="form-select rounded-lg border-border bg-background text-foreground text-sm"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actifs uniquement</option>
            <option value="inactive">Inactifs uniquement</option>
          </select>
        </div>
      </Card>

      {/* Table */}
      <Card noPadding>
        <Table
          columns={columns}
          data={filteredEntries}
          loading={loading}
          emptyTitle="Aucune écriture récurrente"
          emptyMessage="Créez votre première écriture récurrente pour automatiser vos saisies comptables."
        />
      </Card>

      {/* Modals */}
      <RecurringEntryFormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreate}
      />

      <RecurringEntryFormModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedEntry(null);
        }}
        onSubmit={handleEdit}
        entry={selectedEntry}
      />

      <RecurringEntryDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedEntry(null);
        }}
        entry={selectedEntry}
      />
    </div>
  );
}
